import { useState } from "react";
import {
  CONSENT_PRIVACY_LABEL,
  CONSENT_PRIVACY_LABEL_PRIVAT,
  CONSENT_TRAINING_HINT,
  CONSENT_TRAINING_LABEL,
  DATENSCHUTZ_URL,
  PHOTO_NOTICE_IN_CONSENT,
  photoNoticeText,
} from "@umzugsscan/shared";
import { useDraft, useStore } from "../hooks.ts";
import { useNav } from "../Erfassung.tsx";
import { Check, Footer } from "../ui/elemente.tsx";

/**
 * Willkommen als reine Ansicht: einmal für eine laufende Aufnahme
 * (Willkommen) und einmal vor dem Start über den festen Firmenlink
 * (Vorstart in App.tsx) – dort entsteht die Aufnahme erst mit "Los geht's".
 *
 * Seit 12.10.2026 der einzige Schritt vor dem ersten Foto: Der Hinweis zu den
 * Fotos (KI, Löschfrist, keine Personen) steht hier, seine Bestätigung hängt an
 * der Pflicht-Einwilligung. Die freiwillige Einwilligung bleibt getrennt.
 */
export function WillkommenAnsicht(props: {
  companyName: string;
  /** Löschfrist der Fotos in Tagen (Hinweis vor dem ersten Foto). */
  retentionDays: number;
  /** Kunden-Version: ohne Firma, Umzugsscan ist verantwortlich. */
  privat?: boolean;
  /** Demo-Weg: Umzugsunternehmen probieren die Aufnahme selbst aus. */
  demo?: boolean;
  consentPrivacy: boolean;
  consentTraining: boolean;
  onPrivacy: (v: boolean) => void;
  onTraining: (v: boolean) => void;
  onStart: () => void;
  busy?: boolean;
  error?: string | null;
}) {
  const [tried, setTried] = useState(false);
  const ok = props.consentPrivacy;
  return (
    <>
      <section className="intro">
        <h1>Ihre Wohnung in etwa 15 Minuten aufnehmen</h1>
        {props.demo ? (
          <>
            <p className="demo-hinweis">Demo für Umzugsunternehmen</p>
            <p>
              So erlebt Ihr Kunde die Aufnahme. Fotografieren Sie ein, zwei Räume oder nehmen Sie unsere <strong>Beispielfotos</strong>. Nach dem Absenden
              sehen Sie den Bericht, den Ihre Firma bekommt. Alles wird nach 24 Stunden gelöscht.
            </p>
          </>
        ) : props.privat ? (
          <p>
            Fotografieren Sie Ihre Räume mit dem Handy. Nach dem Abschließen ermittelt unsere künstliche Intelligenz für
            1,99 € aus Fotos und Angaben das Umzugsvolumen und den Umfang Ihres Umzugs. Mit dem PDF können Umzugsfirmen Ihnen ein
            Angebot machen, ohne zur Besichtigung zu kommen.
          </p>
        ) : (
          <p>
            <strong>{props.companyName}</strong> braucht für ein Angebot einen Eindruck Ihres Haushalts. Statt eines
            Besichtigungstermins fotografieren Sie Ihre Räume einfach mit dem Handy.
          </p>
        )}
        <p className="hint">
          {props.privat
            ? "Wir führen Sie Raum für Raum. Alles wird laufend gespeichert; mit Ihrer E-Mail-Adresse kommen Sie später wieder hierher."
            : "Wir führen Sie Raum für Raum. Alles wird laufend gespeichert; über denselben Link machen Sie später weiter."}
        </p>
      </section>

      <section className="card">
        <h2>Zu Ihren Fotos</h2>
        <ul className="hinweise">
          {photoNoticeText(props.retentionDays).map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <Check checked={props.consentPrivacy} onChange={props.onPrivacy}>
          {props.privat ? CONSENT_PRIVACY_LABEL_PRIVAT : CONSENT_PRIVACY_LABEL} {PHOTO_NOTICE_IN_CONSENT} <span className="req">(erforderlich)</span>
        </Check>
        <p className="hint">
          Mehr dazu in der{" "}
          <a href={DATENSCHUTZ_URL} target="_blank" rel="noopener">
            Datenschutzerklärung
          </a>
          .
        </p>
        {tried && !ok && <p className="error">Ohne diese Zustimmung können wir leider {props.privat ? "keine Aufnahme" : "kein Angebot"} erstellen.</p>}
        {props.error && <p className="error">{props.error}</p>}
      </section>

      {props.demo ? null : (
        <div className="freiwillig">
          <Check checked={props.consentTraining} onChange={props.onTraining} hint={CONSENT_TRAINING_HINT}>
            {CONSENT_TRAINING_LABEL}
          </Check>
        </div>
      )}

      <Footer
        nextLabel={props.busy ? "Einen Moment …" : "Los geht's"}
        nextDisabled={props.busy}
        onNext={() => {
          setTried(true);
          if (ok) props.onStart();
        }}
      />
    </>
  );
}

export function Willkommen() {
  const d = useDraft();
  const store = useStore();
  const { weiter } = useNav();
  return (
    <WillkommenAnsicht
      companyName={d.company.name}
      retentionDays={d.company.photoRetentionDays ?? 90}
      privat={d.company.privat}
      demo={d.company.demo}
      consentPrivacy={d.survey.consentPrivacy}
      consentTraining={d.survey.consentTraining}
      onPrivacy={(v) => store.updateSurvey({ consentPrivacy: v })}
      onTraining={(v) => store.updateSurvey({ consentTraining: v })}
      onStart={() => {
        store.updateSurvey({ photoNotice: true });
        weiter();
      }}
    />
  );
}
